import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Peacock components
import { TabsNavigation, Tab, Breadcrumb } from 'pepsico-ds';

// Custom Components
import { OnboardingStatusHeader } from '@/components/features/onboarding-status/OnboardingStatusHeader';
import OktaStatusTable from '@/containers/OnboardingStatus/OktaStatusTable';
import DateRangeSelector from '@/components/common/DateRangeSelector/DateRangeSelector';

// Constants
import { ROUTES } from '@/utils/constants/routesConstants';

const OktaStatusView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useParams();

  const [dateRange, setDateRange] = useState({
    startDate: new Date(2023, 0, 1), // Jan 1, 2023
    endDate: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    })(),
    isValid: true
  });

  const shouldLoadData = useMemo(() => {
    return !!dateRange.startDate && !!dateRange.endDate && dateRange.isValid;
  }, [dateRange]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Map status strings to tab indices and routes
  const statusMap = useMemo(
    () => ({
      active: { index: 0, route: ROUTES.ONBOARDING_STATUS_OKTA_ACTIVE },
      'locked-out': {
        index: 1,
        route: ROUTES.ONBOARDING_STATUS_OKTA_LOCKED_OUT
      },
      suspended: { index: 2, route: ROUTES.ONBOARDING_STATUS_OKTA_SUSPENDED },
      inactive: { index: 3, route: ROUTES.ONBOARDING_STATUS_OKTA_INACTIVE },
      deprovisioned: {
        index: 4,
        route: ROUTES.ONBOARDING_STATUS_OKTA_DEPROVISIONED
      }
    }),
    []
  );

  // Effect to sync the selected tab with the current URL status parameter
  useEffect(() => {
    const currentStatusInfo = statusMap[status] || statusMap.active;
    setSelectedTabIndex(currentStatusInfo.index);
  }, [status, navigate, statusMap]);

  // Handler for tab click events
  const handleTabClick = index => {
    // Find the status info corresponding to the clicked index
    const targetStatusInfo = Object.values(statusMap).find(
      info => info.index === index
    );
    if (targetStatusInfo) {
      navigate(targetStatusInfo.route);
    } else {
      navigate(statusMap.active.route);
    }
  };

  return (
    <div>
      <Breadcrumb
        breadcrumbs={[
          { label: 'Onboarding', href: '/' },
          { label: 'Onboarding Status', href: ROUTES.ONBOARDING_STATUS },
          { label: 'Okta Status', href: location.pathname }
        ]}
      />

      <DateRangeSelector
        onDatesChange={setDateRange}
        initialStartDate={dateRange.startDate}
        initialEndDate={dateRange.endDate}
      />

      {!shouldLoadData ? (
        <div className='text-center p-8'>
          <p>Please select both start and end dates to load the table data.</p>
        </div>
      ) : (
        <>
          <OnboardingStatusHeader
            title="Customers' Okta Status"
            className='pl-6 pt-6'
          />

          <div
            className='pt-4 px-6'
            style={{
              backgroundColor: 'var(--color-background-layer-01-dark)'
            }}>
            <TabsNavigation
              isDark={true}
              selectedIndex={selectedTabIndex}
              className='!bg-transparent'>
              <Tab
                className=''
                label='Active'
                onClick={() => handleTabClick(0)}
              />
              <Tab
                className=''
                label='Locked Out'
                onClick={() => handleTabClick(1)}
              />
              <Tab
                className=''
                label='Suspended'
                onClick={() => handleTabClick(2)}
              />
              <Tab
                className=''
                label='Inactive'
                onClick={() => handleTabClick(3)}
              />
              <Tab
                className=''
                label='Deprovisioned'
                onClick={() => handleTabClick(4)}
              />
            </TabsNavigation>
          </div>

          <div className=''>
            <OktaStatusTable
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default OktaStatusView;
